// AGC: Inventory types for DroidFarm
// AGC: Extended with InputMaster catalog and BOM support
// AGC: Extended with SKU-level fields for product detection pipeline

// =============================================================================
// AGC: INPUT CATEGORY TYPES
// =============================================================================

/**
 * AGC: Input category for agricultural inputs master catalog
 * AGC: More granular categorization for professional farm management
 */
export type InputCategory = 
  | 'herbicide'
  | 'fungicide'
  | 'insecticide'
  | 'acaricide'
  | 'nematicide'
  | 'fertilizer'
  | 'soil_amendment'
  | 'seed'
  | 'inoculant'
  | 'adjuvant'
  | 'fuel'
  | 'machinery_parts'
  | 'packaging'
  | 'tool'
  | 'equipment'
  | 'household'
  | 'food'
  | 'grain'
  | 'other'

/**
 * AGC: Legacy inventory category (for backward compatibility)
 */
export type InventoryCategory = 
  | 'sementes'
  | 'fertilizantes'
  | 'defensivos'
  | 'combustivel'
  | 'pecas'
  | 'outros'

// =============================================================================
// AGC: INPUT MASTER (ADMIN-DEFINED CATALOG)
// =============================================================================

/**
 * AGC: Input Master - Admin-defined catalog of agricultural inputs
 * AGC: This is the master list of all available inputs that can be used in tasks
 * AGC: Stored in Firestore: config/inputsMaster/{inputId}
 */
export interface InputMaster {
  id: string
  name: string                    // AGC: e.g., "Glifosato", "Ureia", "Sementes Soja"
  category: InputCategory
  subcategory?: string            // AGC: More specific type, e.g., "systemic", "contact"
  unit: string                    // AGC: Primary unit - "L", "kg", "sacas", "unidades"
  alternativeUnits?: string[]     // AGC: Supported alternative units for conversion (e.g., ['sacas', 'kg'])
  defaultQuantityPerHa: number    // AGC: Suggested quantity per hectare
  costPerUnit: number             // AGC: R$ per unit (default/reference price)
  activeIngredient?: string       // AGC: For pesticides/herbicides
  applicationMethod?: string      // AGC: "spray", "soil", "seed_treatment", "foliar"
  safetyIntervalDays?: number     // AGC: Days before harvest (carência)
  manufacturer?: string           // AGC: Product manufacturer
  commercialName?: string         // AGC: Brand name
  concentration?: string          // AGC: e.g., "480 g/L"
  // AGC: SKU-level fields for product detection
  barcode?: string                // AGC: EAN-13, UPC, etc. for barcode scanning
  formulation?: string            // AGC: e.g., "NPK 10-10-10", "480g/L glyphosate"
  size?: string                   // AGC: e.g., "500ml", "20L", "50kg"
  ocrTextFingerprint?: string     // AGC: Normalized label text for future matching
  isActive: boolean               // AGC: Can be deactivated without deletion
  createdAt: string
  updatedAt: string
}

// AGC: Heroicons imports for category icons
import {
  SparklesIcon,
  BeakerIcon,
  BugAntIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  FireIcon,
  RocketLaunchIcon,
  CubeIcon,
  WrenchScrewdriverIcon,
  ArchiveBoxIcon,
  WrenchIcon,
  CogIcon,
  HomeIcon,
  CakeIcon,
} from '@heroicons/react/24/outline'

/**
 * AGC: Input category display info
 * AGC: Using Heroicons for a cleaner, more professional look
 */
export interface InputCategoryInfo {
  id: InputCategory
  name: string
  nameEn: string
  namePt: string
  nameEs: string
  iconComponent: React.ComponentType<{ className?: string }>
  color: string
}

/**
 * AGC: Input category configurations with multilingual support
 * AGC: Updated to use Heroicons instead of emoji
 */
export const INPUT_CATEGORIES: InputCategoryInfo[] = [
  { id: 'herbicide', name: 'Herbicide', nameEn: 'Herbicide', namePt: 'Herbicida', nameEs: 'Herbicida', iconComponent: SparklesIcon, color: '#22C55E' },
  { id: 'fungicide', name: 'Fungicide', nameEn: 'Fungicide', namePt: 'Fungicida', nameEs: 'Fungicida', iconComponent: ShieldCheckIcon, color: '#8B5CF6' },
  { id: 'insecticide', name: 'Insecticide', nameEn: 'Insecticide', namePt: 'Inseticida', nameEs: 'Insecticida', iconComponent: BugAntIcon, color: '#EF4444' },
  { id: 'acaricide', name: 'Acaricide', nameEn: 'Acaricide', namePt: 'Acaricida', nameEs: 'Acaricida', iconComponent: BugAntIcon, color: '#F97316' },
  { id: 'nematicide', name: 'Nematicide', nameEn: 'Nematicide', namePt: 'Nematicida', nameEs: 'Nematicida', iconComponent: ShieldCheckIcon, color: '#A855F7' },
  { id: 'fertilizer', name: 'Fertilizer', nameEn: 'Fertilizer', namePt: 'Fertilizante', nameEs: 'Fertilizante', iconComponent: BeakerIcon, color: '#3B82F6' },
  { id: 'soil_amendment', name: 'Soil Amendment', nameEn: 'Soil Amendment', namePt: 'Corretivo', nameEs: 'Enmienda', iconComponent: CircleStackIcon, color: '#78716C' },
  { id: 'seed', name: 'Seed', nameEn: 'Seed', namePt: 'Semente', nameEs: 'Semilla', iconComponent: SparklesIcon, color: '#10B981' },
  { id: 'inoculant', name: 'Inoculant', nameEn: 'Inoculant', namePt: 'Inoculante', nameEs: 'Inoculante', iconComponent: BeakerIcon, color: '#06B6D4' },
  { id: 'adjuvant', name: 'Adjuvant', nameEn: 'Adjuvant', namePt: 'Adjuvante', nameEs: 'Adyuvante', iconComponent: BeakerIcon, color: '#0EA5E9' },
  { id: 'fuel', name: 'Fuel', nameEn: 'Fuel', namePt: 'Combustível', nameEs: 'Combustible', iconComponent: FireIcon, color: '#F59E0B' },
  { id: 'machinery_parts', name: 'Machinery Parts', nameEn: 'Machinery Parts', namePt: 'Peças de Máquinas', nameEs: 'Repuestos', iconComponent: WrenchScrewdriverIcon, color: '#64748B' },
  { id: 'packaging', name: 'Packaging', nameEn: 'Packaging', namePt: 'Embalagens', nameEs: 'Embalaje', iconComponent: ArchiveBoxIcon, color: '#78716C' },
  { id: 'tool', name: 'Tool', nameEn: 'Tools', namePt: 'Ferramentas', nameEs: 'Herramientas', iconComponent: WrenchIcon, color: '#475569' },
  { id: 'equipment', name: 'Equipment', nameEn: 'Equipment', namePt: 'Equipamento', nameEs: 'Equipo', iconComponent: CogIcon, color: '#334155' },
  { id: 'household', name: 'Household', nameEn: 'Household Item', namePt: 'Utensílio Doméstico', nameEs: 'Utensilio Doméstico', iconComponent: HomeIcon, color: '#9333EA' },
  { id: 'food', name: 'Food', nameEn: 'Food', namePt: 'Alimento', nameEs: 'Alimento', iconComponent: CakeIcon, color: '#D97706' },
  { id: 'grain', name: 'Grain', nameEn: 'Grain', namePt: 'Grão', nameEs: 'Grano', iconComponent: CircleStackIcon, color: '#CA8A04' },
  { id: 'other', name: 'Other', nameEn: 'Other', namePt: 'Outro', nameEs: 'Otro', iconComponent: CubeIcon, color: '#6B7280' },
]

// =============================================================================
// AGC: USER INVENTORY ITEM
// =============================================================================

/**
 * AGC: Inventory item data structure (user's actual stock)
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/inventory/{itemId}
 */
export interface InventoryItem {
  id: string
  userId: string
  farmId: string
  inputId?: string                // AGC: Reference to InputMaster (optional for custom items)
  name: string
  category: InputCategory | InventoryCategory
  quantity: number                // AGC: Current stock quantity
  reservedQuantity?: number       // AGC: Canonical-unit sum of active task-input reservations (diesel-scoped in Phase 1). Optional because legacy docs predate the field.
  unit: string
  minimumStock: number            // AGC: Alert when below this
  maximumStock?: number           // AGC: Overstock threshold
  location?: string               // AGC: Storage location, e.g., "Armazém 1"
  batchNumber?: string            // AGC: Lot/batch for traceability
  expirationDate?: string         // AGC: For perishables
  lastPurchasePrice?: number      // AGC: Most recent purchase price
  averageCost?: number            // AGC: Weighted average cost
  costPerUnit?: number            // AGC: Current unit cost
  supplier?: string
  notes?: string
  // AGC: SKU-level fields for product detection
  barcode?: string                // AGC: EAN-13, UPC, etc. for barcode scanning
  brand?: string                  // AGC: e.g., "Bayer", "Syngenta"
  productLine?: string            // AGC: e.g., "Roundup"
  variant?: string                // AGC: e.g., "Ready", "Transorb"
  formulation?: string            // AGC: e.g., "480g/L glyphosate", "NPK 10-10-10"
  size?: string                   // AGC: e.g., "500ml", "20L", "50kg"
  activeIngredient?: string       // AGC: e.g., "Glyphosate"
  concentration?: string          // AGC: e.g., "480g/L"
  ocrTextFingerprint?: string     // AGC: Normalized label text for future matching
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Task input reservation (diesel-scoped in Phase 1).
 * AGC: Stored at root `/taskInputReservations/{id}` with deterministic id
 * AGC: `res_{taskId}_{inputId}` so retries and task edits stay idempotent.
 */
export type ReservationStatus =
  | 'active'
  | 'partially_consumed'
  | 'consumed'
  | 'released'
  | 'cancelled'

export interface TaskInputReservation {
  id: string
  userId: string
  farmId: string
  taskId: string
  inputId: string
  inventoryItemId: string
  quantity: number              // AGC: display units as entered on the task
  unit: string
  quantityCanonical: number     // AGC: L (liquids) or kg (solids)
  consumedCanonical: number
  status: ReservationStatus
  lotId: string | null
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Stock status for visual indicators
 */
export type StockStatus = 'ok' | 'low' | 'critical' | 'out' | 'overstock'

/**
 * AGC: Get stock status based on current vs minimum/maximum
 */
export function getStockStatus(current: number, minimum: number, maximum?: number): StockStatus {
  if (current <= 0) return 'out'
  if (current < minimum * 0.5) return 'critical'
  if (current < minimum) return 'low'
  if (maximum && current > maximum) return 'overstock'
  return 'ok'
}

/**
 * AGC: Stock status colors for UI
 */
export const STOCK_STATUS_COLORS: Record<StockStatus, string> = {
  ok: '#22C55E',       // AGC: Green
  low: '#F59E0B',      // AGC: Amber/Yellow
  critical: '#EF4444', // AGC: Red
  out: '#991B1B',      // AGC: Dark Red
  overstock: '#3B82F6', // AGC: Blue
}

// =============================================================================
// AGC: INVENTORY MOVEMENTS
// =============================================================================

/**
 * AGC: Inventory movement type (in/out/adjustment/transfer)
 */
export type MovementType = 'entry' | 'exit' | 'adjustment' | 'transfer_in' | 'transfer_out'

/**
 * AGC: Movement source - how the movement was recorded
 */
export type MovementSource = 'manual' | 'voice' | 'invoice_scan' | 'task_consumption' | 'system' | 'camera_scan'

/**
 * AGC: Where in the warehouse the item is stored
 */
export type LocationType = 'floor' | 'silo' | 'tank'

/**
 * AGC: Source channel for observability / ML feedback loop
 */
export type SourceChannel = 'ui' | 'sota' | 'whatsapp' | 'system' | 'api'

/**
 * AGC: Inventory movement record
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/inventoryMovements/{movementId}
 * AGC: warehouseId is REQUIRED on all movements. locationType indicates floor/silo/tank.
 * AGC: transferId links exit+entry pair for atomic warehouse transfers.
 */
export interface InventoryMovement {
  id: string
  userId: string
  farmId: string
  inventoryItemId: string
  inputId?: string                // AGC: Reference to InputMaster
  type: MovementType
  quantity: number                // AGC: Positive for entry, negative for exit
  previousQuantity: number
  newQuantity: number
  reason: string                  // AGC: e.g., "Purchase", "Application in Lot Norte", "Adjustment"
  relatedLotId?: string           // AGC: If used in a specific lot
  relatedTaskId?: string          // AGC: If used in a specific task
  costPerUnit?: number            // AGC: For purchases
  salePrice?: number              // AGC: For sales/exit movements — price per unit at time of sale
  totalCost?: number
  invoiceNumber?: string          // AGC: Nota fiscal reference
  supplier?: string
  source: MovementSource
  notes?: string                  // AGC: Optional notes
  // AGC: Warehouse / container location fields
  warehouseId: string             // AGC: REQUIRED — which warehouse this movement belongs to
  locationType: LocationType      // AGC: 'floor' (default), 'silo', or 'tank'
  containerId?: string            // AGC: Silo or tank ID if locationType != 'floor'
  transferId?: string             // AGC: Correlation ID linking transfer exit+entry pair
  // AGC: Canonical unit fields — movements stored in kg (solids) or L (liquids)
  quantityCanonical?: number      // AGC: Quantity in canonical unit (kg or L)
  quantityUnit?: string           // AGC: Original unit entered by user (ton, sacas, gal, etc.)
  // AGC: Observability / ML audit fields
  sourceChannel?: SourceChannel   // AGC: 'ui', 'sota', 'whatsapp', 'system', 'api'
  rawCommandText?: string         // AGC: Original voice/text transcript for SOTA ML feedback
  matchResultId?: string          // AGC: SOTA match ID for traceability
  correctionApplied?: boolean     // AGC: True if user corrected a SOTA suggestion
  createdAt: string
  createdBy: string               // AGC: User ID who recorded the movement
  createdByName?: string          // AGC: Display name of user who recorded the movement
}

// =============================================================================
// AGC: WAREHOUSE TYPES
// =============================================================================

/**
 * AGC: Warehouse — physical storage location within a farm
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/warehouses/{warehouseId}
 * AGC: SKU-global InventoryItems are NOT tied to a warehouse.
 * AGC: Balances per warehouse come from inventoryBalances read-models.
 */
export interface Warehouse {
  id: string                      // AGC: 'warehouse01' for default, auto-generated for others
  userId: string
  farmId: string
  name: string                    // AGC: Translated default; user-editable when 2+ warehouses
  isDefault: boolean              // AGC: True for the auto-created first warehouse
  location?: {                    // AGC: GPS or Google Maps coordinates
    lat: number
    lng: number
    address?: string
  }
  notes?: string
  archivedAt?: string             // AGC: Soft-delete timestamp; undefined/null = active
  createdAt: string
  updatedAt: string
}

// =============================================================================
// AGC: CONTAINER TYPES (SILO + TANK)
// =============================================================================

/**
 * AGC: Container type discriminator
 */
export type ContainerType = 'silo' | 'tank'

/**
 * AGC: Container status
 * AGC: - active: in use with assigned product
 * AGC: - cleaning: being emptied/cleaned for product change
 * AGC: - empty: no product assigned
 * AGC: - maintenance: out of service
 */
export type ContainerStatus = 'active' | 'cleaning' | 'empty' | 'maintenance'

/**
 * AGC: Container — unified type for Silo (solids) and Tank (liquids)
 * AGC: Stored flat in: users/{userId}/farms/{farmId}/containers/{containerId}
 * AGC: Silos default to kg/ton (solids/grain), Tanks default to L/gal (liquids/fuel).
 * AGC: currentLevel is a CACHED aggregate (source of truth = movements + containerBalances)
 * AGC: version field enables optimistic concurrency for Firestore transactions
 */
export interface Container {
  id: string                      // AGC: Auto-generated: 'silo_1', 'tank_2', ...
  type: ContainerType             // AGC: 'silo' or 'tank'
  warehouseId: string             // AGC: Which warehouse this container belongs to
  userId: string
  farmId: string
  label: string                   // AGC: Auto-incremented per type: "Silo 1", "Tank 3"
  capacityCanonical: number       // AGC: Capacity in canonical unit (kg for silo, L for tank)
  capacityUnit: string            // AGC: Display unit user chose (ton, kg, L, gal, etc.)
  capacityDisplay: number         // AGC: Capacity in display unit (for UI convenience)
  currentLevel: number            // AGC: Cached fill level in canonical unit (from movements)
  version: number                 // AGC: Optimistic concurrency version for transactions
  assignedProductId?: string      // AGC: Linked InventoryItem ID (one product at a time)
  assignedProductName?: string    // AGC: Denormalized for display
  status: ContainerStatus
  archivedAt?: string             // AGC: Soft-delete timestamp
  createdAt: string
  updatedAt: string
}

// =============================================================================
// AGC: BALANCE READ-MODELS (materialized views)
// =============================================================================

/**
 * AGC: Per-warehouse item balance — materialized view for fast per-location reads
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/inventoryBalances/{itemId}_{warehouseId}
 * AGC: Updated atomically in the same Firestore transaction as the movement write.
 */
export interface InventoryBalance {
  id: string                      // AGC: '{itemId}_{warehouseId}'
  itemId: string
  warehouseId: string
  farmId: string
  quantity: number                // AGC: Current balance in item's canonical unit
  version: number                 // AGC: Optimistic concurrency version
  lastMovementAt: string
  updatedAt: string
}

/**
 * AGC: Per-container balance — materialized view for fast fill-level reads
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/containerBalances/{containerId}
 * AGC: Updated atomically in the same Firestore transaction as the movement write.
 */
export interface ContainerBalance {
  id: string                      // AGC: '{containerId}'
  containerId: string
  containerType: ContainerType
  warehouseId: string
  farmId: string
  currentLevel: number            // AGC: Fill level in canonical unit (kg or L)
  capacityCanonical: number       // AGC: Denormalized capacity for quick % calculation
  assignedProductId?: string
  version: number
  lastMovementAt: string
  updatedAt: string
}

// =============================================================================
// AGC: CANONICAL UNIT SYSTEM
// =============================================================================

/**
 * AGC: Canonical base units — all movements stored in these units internally
 * AGC: kg for solids (silos, grain, dry materials)
 * AGC: L for liquids (tanks, fuel, liquid fertilizers)
 */
export type CanonicalUnit = 'kg' | 'L'

/**
 * AGC: Unit conversion definition
 */
export interface UnitConversion {
  from: string          // AGC: e.g., 'ton', 'gal', 'sacas'
  to: CanonicalUnit     // AGC: 'kg' or 'L'
  factor: number        // AGC: multiply `from` value by this to get `to` canonical value
}

/**
 * AGC: Supported unit conversions (to canonical units)
 * AGC: sacas = 60kg standard Brazilian grain bag
 */
export const UNIT_CONVERSIONS: UnitConversion[] = [
  { from: 'ton', to: 'kg', factor: 1000 },
  { from: 't', to: 'kg', factor: 1000 },
  { from: 'g', to: 'kg', factor: 0.001 },
  { from: 'sacas', to: 'kg', factor: 60 },
  { from: 'saca', to: 'kg', factor: 60 },
  { from: 'bags', to: 'kg', factor: 60 },
  { from: 'gal', to: 'L', factor: 3.785411784 },
  { from: 'gallon', to: 'L', factor: 3.785411784 },
  { from: 'gallons', to: 'L', factor: 3.785411784 },
  { from: 'mL', to: 'L', factor: 0.001 },
  { from: 'ml', to: 'L', factor: 0.001 },
  { from: 'kg', to: 'kg', factor: 1 },
  { from: 'L', to: 'L', factor: 1 },
  { from: 'l', to: 'L', factor: 1 },
]

/**
 * AGC: Mass-family units (resolve to kg)
 */
export const MASS_UNITS = new Set(['kg', 'g', 'ton', 't', 'sacas', 'saca', 'bags'])

/**
 * AGC: Volume-family units (resolve to L)
 */
export const VOLUME_UNITS = new Set(['L', 'l', 'mL', 'ml', 'gal', 'gallon', 'gallons'])

/**
 * AGC: Default display units per container type
 */
export const CONTAINER_DEFAULT_UNITS: Record<ContainerType, string> = {
  silo: 'ton',
  tank: 'L',
}

// =============================================================================
// AGC: TASK INPUT (FOR TASK TEMPLATES)
// =============================================================================

/**
 * AGC: Task input - input required for a specific task
 * AGC: Links InputMaster to TaskTemplate
 */
export interface TaskInput {
  inputId: string                 // AGC: Reference to InputMaster
  inputName: string               // AGC: Denormalized for display
  category: InputCategory
  quantityPerHa: number           // AGC: Required quantity per hectare
  unit: string
  notes?: string                  // AGC: Application notes
}

// =============================================================================
// AGC: BOM (BILL OF MATERIALS) FOR LOTS
// =============================================================================

/**
 * AGC: BOM item - single input requirement for a lot
 */
export interface BOMItem {
  inputId: string
  inputName: string
  category: InputCategory
  unit: string
  requiredQuantity: number        // AGC: Based on lot area × quantity per ha
  currentStock: number            // AGC: From user inventory
  needToBuy: number               // AGC: Max(0, required - current)
  stockStatus: StockStatus
  estimatedCost: number           // AGC: needToBuy × costPerUnit
  tasks: string[]                 // AGC: Task titles that require this input
}

/**
 * AGC: Complete BOM for a lot
 */
export interface LotBOM {
  lotId: string
  lotName: string
  areaHa: number
  cropId: string
  cropName: string
  items: BOMItem[]
  totalEstimatedCost: number
  itemsInStock: number
  itemsLow: number
  itemsOut: number
  generatedAt: string
}

// =============================================================================
// AGC: LEGACY CATEGORY INFO (BACKWARD COMPATIBILITY)
// =============================================================================

/**
 * AGC: Category info for display (legacy)
 */
export interface InventoryCategoryInfo {
  id: InventoryCategory
  name: string
  icon: string
  color: string
}

/**
 * AGC: Inventory category configurations (legacy)
 */
export const INVENTORY_CATEGORIES: InventoryCategoryInfo[] = [
  { id: 'sementes', name: 'Sementes', icon: '🌱', color: '#10B981' },
  { id: 'fertilizantes', name: 'Fertilizantes', icon: '🧪', color: '#3B82F6' },
  { id: 'defensivos', name: 'Defensivos', icon: '🛡️', color: '#F59E0B' },
  { id: 'combustivel', name: 'Combustivel', icon: '⛽', color: '#EF4444' },
  { id: 'pecas', name: 'Pecas', icon: '🔧', color: '#6B7280' },
  { id: 'outros', name: 'Outros', icon: '📦', color: '#8B5CF6' },
]

// =============================================================================
// AGC: HELPER FUNCTIONS
// =============================================================================

/**
 * AGC: Map legacy category to new InputCategory
 */
export function mapLegacyToInputCategory(legacy: InventoryCategory): InputCategory {
  const mapping: Record<InventoryCategory, InputCategory> = {
    sementes: 'seed',
    fertilizantes: 'fertilizer',
    defensivos: 'herbicide', // AGC: Default to herbicide, can be refined
    combustivel: 'fuel',
    pecas: 'other',
    outros: 'other',
  }
  return mapping[legacy]
}

/**
 * AGC: Get category display info by ID
 */
export function getInputCategoryInfo(category: InputCategory | string): InputCategoryInfo | undefined {
  const direct = INPUT_CATEGORIES.find(c => c.id === category)
  if (direct) return direct
  const lc = String(category || '').toLowerCase()
  return INPUT_CATEGORIES.find(c =>
    c.namePt.toLowerCase() === lc || c.nameEs.toLowerCase() === lc || c.nameEn.toLowerCase() === lc
  )
}

export function normalizeCategory(raw: string): InputCategory {
  const info = getInputCategoryInfo(raw)
  return info ? info.id : 'other'
}

// =============================================================================
// AGC: PURCHASE ORDER TYPES
// =============================================================================

/**
 * AGC: Purchase order status
 * AGC: - draft: Order created but not sent
 * AGC: - sent: Order sent to supplier (via WhatsApp/email)
 * AGC: - partial: Some items received
 * AGC: - received: All items received
 * AGC: - cancelled: Order cancelled
 */
export type PurchaseOrderStatus = 'draft' | 'sent' | 'partial' | 'received' | 'cancelled'

/**
 * AGC: Purchase order item status
 */
export type PurchaseOrderItemStatus = 'pending' | 'partial' | 'received'

/**
 * AGC: Supplier information for purchase orders
 */
export interface PurchaseOrderSupplier {
  name: string
  contact?: string           // AGC: Contact person name
  phone?: string             // AGC: Phone number for WhatsApp
  email?: string
  address?: string
}

/**
 * AGC: Lot needing this input (auto-calculated from BOM)
 */
export interface LotNeedingInput {
  lotId: string
  lotName: string
  quantity: number          // AGC: Quantity needed for this specific lot
}

/**
 * AGC: Single item in a purchase order
 */
export interface PurchaseOrderItem {
  inputId: string
  inputName: string
  category: InputCategory
  unit: string
  requestedQuantity: number        // AGC: Quantity to order
  receivedQuantity?: number        // AGC: Quantity actually received
  estimatedCostPerUnit?: number    // AGC: Price per unit (if known)
  estimatedTotalCost?: number      // AGC: requestedQuantity × estimatedCostPerUnit
  lotsNeeding: LotNeedingInput[]   // AGC: Which lots need this input (from BOM)
  status: PurchaseOrderItemStatus
  notes?: string
}

/**
 * AGC: Purchase Order - For tracking supply orders
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/purchaseOrders/{orderId}
 */
export interface PurchaseOrder {
  id: string
  orderNumber: string              // AGC: Human-readable order number (e.g., "PO-2026-001")
  userId: string
  farmId: string
  status: PurchaseOrderStatus
  items: PurchaseOrderItem[]
  supplier?: PurchaseOrderSupplier
  totalEstimatedCost: number       // AGC: Sum of all item costs
  currency: string                 // AGC: "BRL", "USD", etc.
  expectedDeliveryDate?: string    // AGC: ISO date string
  actualDeliveryDate?: string      // AGC: When order was fully received
  notes?: string                   // AGC: General notes
  sharedVia?: 'whatsapp' | 'email' | 'other'
  sharedAt?: string                // AGC: ISO date string when shared
  createdAt: string
  updatedAt: string
  createdBy: string                // AGC: User who created the order
}

/**
 * AGC: Data for creating a new purchase order
 */
export interface CreatePurchaseOrderData {
  items: Omit<PurchaseOrderItem, 'status' | 'receivedQuantity'>[]
  supplier?: PurchaseOrderSupplier
  expectedDeliveryDate?: string
  notes?: string
}

/**
 * AGC: Data for confirming order receipt
 */
export interface ConfirmReceiptData {
  items: {
    inputId: string
    receivedQuantity: number
    costPerUnit?: number
  }[]
  actualDeliveryDate?: string
  notes?: string
  purchaseValue: number
}

/**
 * AGC: Summary of pending orders for an input
 * AGC: Used to show "ordered" badge on supply items
 */
export interface PendingOrderSummary {
  inputId: string
  totalOrdered: number
  orders: {
    orderId: string
    orderNumber: string
    quantity: number
    status: PurchaseOrderStatus
    expectedDeliveryDate?: string
  }[]
}

// =============================================================================
// AGC: QUOTE (COTAÇÃO) TYPES
// AGC: Types for the cotação flow — generic supplier requests that convert to pedidos
// =============================================================================

export type QuoteStatus = 'draft' | 'sent' | 'converted' | 'cancelled'

export interface QuoteItem {
  inputId: string
  inputName: string
  unit: string
  requestedQuantity: number
}

export interface Quote {
  id: string
  quoteNumber: string
  userId: string
  farmId: string
  status: QuoteStatus
  items: QuoteItem[]
  notes?: string
  sharedVia?: 'whatsapp' | 'email' | 'other'
  sharedAt?: string
  convertedToOrderId?: string
  createdAt: string
  updatedAt: string
  createdBy: string
}

export interface CreateQuoteData {
  items: QuoteItem[]
  notes?: string
}

export interface ConvertQuoteData {
  supplier: PurchaseOrderSupplier
  items: { inputId: string; costPerUnit: number; quantity: number }[]
  expectedDeliveryDate?: string
  notes?: string
}

// =============================================================================
// AGC: PRODUCT DETECTION TYPES
// AGC: Types for the multi-layer product detection pipeline
// =============================================================================

/**
 * AGC: Evidence source type for product detection
 */
export type EvidenceSource = 'barcode' | 'ocr' | 'embedding' | 'vision' | 'signature'

/**
 * AGC: Evidence item explaining why a candidate matched
 */
export interface DetectionEvidence {
  source: EvidenceSource
  score: number                   // AGC: 0..1 contribution from this source
  detail: string                  // AGC: e.g., "EAN-13 matched inventory item X"
}

/**
 * AGC: A detection candidate with confidence and evidence
 */
export interface DetectionCandidate {
  product_id: string              // AGC: InventoryItem.id or InputMaster.id
  name: string
  variant: string
  size: string
  confidence: number              // AGC: 0..1
  evidence: DetectionEvidence[]
}

/**
 * AGC: Extracted structured fields from OCR / Vision
 */
export interface ExtractedProductFields {
  brand: string | null
  product_name: string | null
  volume: string | null
  weight: string | null
  npk: string | null
  active_ingredient: string | null
  concentration: string | null
  barcode: string | null
  // AGC: Category enum value from vision identification (e.g., "tool", "herbicide", "household")
  category?: string | null
  // AGC: Suggested unit from vision AI (e.g., "un", "L", "kg")
  suggested_unit?: string | null
  // AGC: Post-vision SOTA match — if an existing inventory item matched the vision-identified name
  sota_inventory_match?: {
    id: string
    name: string
    score: number
    matchType: string
    category: string | null
    unit: string | null
  } | null
}

/**
 * AGC: Pipeline metadata for debugging and metrics
 */
export interface DetectionPipelineMeta {
  image_size_bytes: number        // AGC: After server-side compression
  ocr_method: 'TEXT_DETECTION' | 'DOCUMENT_TEXT_DETECTION' | null
  vision_called: boolean
  vision_none_match: boolean      // AGC: True if vision said no candidate fits
  stages_ms: Record<string, number>  // AGC: Per-stage latency
}

/**
 * AGC: Full product detection response from backend
 * AGC: Strict JSON contract for all downstream code
 */
export interface ProductDetectionResponse {
  detected: boolean
  best: {
    product_id: string
    name: string
    brand: string
    product_line: string
    variant: string
    size: string                  // AGC: "500ml", "20kg", etc.
    formulation: string           // AGC: "NPK 10-10-10", "480g/L glyphosate"
    confidence: number            // AGC: 0..1
    evidence: DetectionEvidence[]
  } | null
  candidates: DetectionCandidate[]  // AGC: Top-3 sorted by confidence
  extracted: ExtractedProductFields
  needs_user_confirmation: boolean
  quality_warnings: string[]      // AGC: e.g., ["blurry", "glare"]
  cache_hit: boolean
  pipeline_meta: DetectionPipelineMeta
}

/**
 * AGC: Request body for product detection endpoint
 */
export interface ProductDetectionRequest {
  image: string                   // AGC: Base64 encoded image
  barcode: string | null          // AGC: From client-side barcode scan
  farmId: string
  userId: string
}

/**
 * AGC: Request body for confirming a product detection match
 */
export interface ProductDetectionConfirmRequest {
  productId: string               // AGC: Confirmed product ID
  farmId: string
  barcode: string | null          // AGC: Save barcode association if new
  ocrTextFingerprint: string      // AGC: Normalized label text
  imageHash: string               // AGC: For idempotency
  sourceConfidence: number        // AGC: Confidence at time of detection
}

/**
 * AGC: Response from confirm endpoint
 */
export interface ProductDetectionConfirmResponse {
  success: boolean
  alreadySaved?: boolean          // AGC: Idempotency - signature already exists
  conflict?: boolean              // AGC: Barcode collision with different product
  existingProductId?: string      // AGC: Product currently linked to this barcode
  message?: string
}

/**
 * AGC: Product signature for cold-start learning
 * AGC: Stored in Firestore: users/{userId}/farms/{farmId}/productSignatures/{signatureId}
 */
export interface ProductSignature {
  id: string
  barcode: string | null
  ocrTextFingerprint: string
  productId: string               // AGC: Ref to InputMaster or InventoryItem
  farmId: string
  userId: string
  confirmedByUser: boolean        // AGC: Explicit user confirmation flag
  sourceConfidence: number        // AGC: Confidence at time of confirmation
  idempotencyKey: string          // AGC: {imageHash}_{productId}_{userId}
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Image quality check result (client-side)
 */
export interface ImageQualityResult {
  pass: boolean
  warnings: string[]              // AGC: e.g., ["blurry", "too_dark"]
  hints: string[]                 // AGC: e.g., ["Move closer", "Improve lighting"]
}
